import { useState } from "react";
import Button from "@/common/ui/Button";
import { LightText2 } from "@/common/ui/Text";
import { toastMessage } from "@/utils/helper/myToast";
import { exportTransactions } from "@/services/payment.services";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import CrossIcon from "@/assets/CrossIcon";
import CheckIcon from "@/assets/CheckIcon";

interface ExportConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  exportType: "selected" | "all";
  selectedCount: number;
  totalCount: number;
  isSelectAllActive: boolean;
  transactionIds: string[];
  onSuccess?: () => void;
}

// ----------------- HELPER -----------------
function generateExportMessage({
  exportType,
  selectedCount,
  totalCount,
  isSelectAllActive,
}: {
  exportType: "selected" | "all";
  selectedCount: number;
  totalCount: number;
  isSelectAllActive: boolean;
}): { content: string; successToast: string; showOkOnly: boolean } {
  if (exportType === "selected") {
    if (isSelectAllActive) {
      return {
        content: `Are you sure you want to export all ${totalCount} ${totalCount === 1 ? 'payment' : 'payments'}?`,
        successToast: "Your Export has been scheduled and we will notify you once completed, check export history for updates.",
        showOkOnly: false,
      };
    } else {
      return {
        content: `Are you sure you want to export ${selectedCount} ${selectedCount === 1 ? 'payment' : 'payments'}?`,
        successToast: "Your Export has been scheduled and we will notify you once completed, check export history for updates.",
        showOkOnly: false,
      };
    }
  }

  if (exportType === "all") {
    return {
      content: `Are you sure you want to export all ${totalCount} ${totalCount === 1 ? 'payment' : 'payments'}?`,
      successToast: "Your Export has been scheduled and we will notify you once completed, check export history for updates.",
      showOkOnly: false,
    };
  }

  return {
    content: "Are you sure you want to export the selected payments?",
    successToast: "Your Export has been scheduled and we will notify you once completed, check export history for updates.",
    showOkOnly: false,
  };
}

// ----------------- COMPONENT -----------------
const ExportConfirmationModal = ({
  isOpen,
  onClose,
  exportType,
  selectedCount,
  totalCount,
  isSelectAllActive,
  transactionIds,
  onSuccess,
}: ExportConfirmationModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const message = generateExportMessage({ 
    exportType, 
    selectedCount, 
    totalCount, 
    isSelectAllActive 
  });

  const handleExport = async () => {
    setIsLoading(true);
    try {
      let idsToSend: string[] = [];
      let mode: string = '';
      
      if (exportType === "selected") {
        // Check if select all is active
        if (isSelectAllActive) {
          // Export all records when select all is active
          idsToSend = [];
          mode = 'all';
        } else {
          // Export only selected rows
          idsToSend = transactionIds;
          mode = 'selected';
        }
      } else if (exportType === "all") {
        // Export all - send empty array with mode 'all'
        idsToSend = [];
        mode = 'all';
      }
      
      const result = await exportTransactions(idsToSend, mode);
      
      console.log("Export result:", result); // Debug log
      
      if (result?.status === 200) {
        // Show success toast with export history link first
        toast.success(
          (t) => (
            <div className="flex items-center justify-between w-full gap-2">
              <div className="flex flex-col gap-1">
                <span className="text-xs md:text-sm !leading-normal font-bold">
                  Your Export has been scheduled and we will notify you once completed, check{" "}
                  <button
                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    onClick={() => {
                      toast.dismiss(t.id);
                      navigate("/home/Payments-history/export-history");
                    }}
                  >
                    export history
                  </button>{" "}
                  for updates.
                </span>
              </div>
              <button onClick={() => toast.dismiss(t.id)} className="py-1 hover:scale-105">
                <CrossIcon />
              </button>
            </div>
          ),
          {
            duration: 5000,
            position: "top-center",
            style: {
              width: "fit-content",
              maxWidth: "90%",
              padding: "12px",
            },
            icon: (
              <>
                <div className="gap-2 h-full flex items-center justify-center">
                  <div className="bg-text-green rounded-50 h-full w-1"></div>
                  <CheckIcon />
                </div>
              </>
            ),
          }
        );
        
        // Then close modal and call success callback
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else {
        console.log("Error", result);
        const { message: msg, isError } = result?.response?.data || {};
        if (msg) return toastMessage(msg, isError);
        if (result.message) return toastMessage(result.message, true);
        toastMessage("Failed to export payments. Please try again.", true);
        onClose();
      }
    } catch (error) {
      console.error("Error exporting payments", error);
      toastMessage("Failed to export payments. Please try again.", true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-3 md:p-4 relative mx-4">
        <LightText2 text={message.content} />

        <div className="flex justify-end pt-5">
          {message.showOkOnly ? (
            <Button variant="outline" size="small" onClick={onClose}>
              Ok
            </Button>
          ) : (
            <div className="flex justify-between gap-4">
              <Button variant="outline" size="small" onClick={onClose}>
                No
              </Button>
              <Button
                variant="outline-danger"
                size="small"
                isLoading={isLoading}
                onClick={handleExport}
                disabled={isLoading}
              >
                Yes
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportConfirmationModal;
